import axios from 'axios';
import logger from '../utils/logger';
import { SessionQuestionEntityFrontend, WorkerWithSessionQuestionsResponse } from './sessionQuestionsApi';
import { Language } from '../models/session';

const baseURL =
    import.meta.env.VITE_API_URL ||
    import.meta.env.VITE_API_BASE_URL ||
    'http://localhost:3001';

const publicAxiosInstance = axios.create({
    baseURL,
    withCredentials: false,
    headers: {
        'Content-Type': 'application/json',
    },
});

export interface SessionShareSummary {
    language: Language;
}

export interface PublicBulkUpdateAnswerItem {
    sessionQuestionId: number;
    answer: string;
}

export const fetchSessionShareSummary = async (
    publicShareId: string
): Promise<SessionShareSummary> => {
    try {
        logger.debug(`Fetching public session summary for ${publicShareId}`);
        const response = await publicAxiosInstance.get<SessionShareSummary>(
            `/sessions/share/${publicShareId}`
        );
        return { language: response.data.language };
    } catch (error: any) {
        logger.error(`Failed to fetch session summary for shareId ${publicShareId}`, error);
        throw error;
    }
};

/**
 * Backend endpoint shape changed in PR1 (worker-as-truth) — returns
 * `{ worker, questions }` instead of bare `SessionQuestionEntityFrontend[]`.
 * Use the wrapped fetcher when you need to derive answer values from worker.
 */
export const fetchSessionShareQuestionsWithWorker = async (
    publicShareId: string,
): Promise<WorkerWithSessionQuestionsResponse> => {
    try {
        logger.debug(`Fetching public session worker + questions for ${publicShareId}`);
        const response = await publicAxiosInstance.get<WorkerWithSessionQuestionsResponse>(
            `/sessions/share/${publicShareId}/questions`,
        );
        return response.data;
    } catch (error: any) {
        logger.error(`Failed to fetch session questions for shareId ${publicShareId}`, error);
        throw error;
    }
};

/**
 * Compatibility wrapper for callers that only need the question metadata array.
 */
export const fetchSessionShareQuestions = async (
    publicShareId: string,
): Promise<SessionQuestionEntityFrontend[]> => {
    const wrapped = await fetchSessionShareQuestionsWithWorker(publicShareId);
    return wrapped.questions;
};

export const submitSessionShareAnswers = async (
    publicShareId: string,
    updates: PublicBulkUpdateAnswerItem[]
): Promise<void> => {
    try {
        logger.debug(
            `Submitting ${updates.length} session question updates via public share ${publicShareId}`
        );
        await publicAxiosInstance.post(
            `/sessions/share/${publicShareId}/questions/bulk-update`,
            updates
        );
        logger.debug('Public session answers submitted successfully');
    } catch (error: any) {
        logger.error(`Failed to submit session answers for shareId ${publicShareId}`, error);
        throw error;
    }
};

export type PublicSessionFileType =
    | 'CV'
    | 'VCA'
    | 'RIJBEWIJS'
    | 'REFERENCE'
    | 'ID_DOCUMENT'
    | 'CERTIFICATION'
    | 'OTHER';

export interface UploadSessionShareFileResponse {
    workerFileId: number;
    fileType: PublicSessionFileType;
    status: 'uploaded';
}

export interface AiSuggestionItem {
    field: string;
    label: string;
    suggestedValue: string;
    source: string;
    confidence?: number;
}

export interface AiPrefillResponse {
    suggestions: AiSuggestionItem[];
    errors: string[];
    processedBy: string;
}

export interface ApplyAiPrefillResponse {
    applied: number;
    skipped: number;
}

export const runAiPrefillForPublicShare = async (
    publicShareId: string,
): Promise<AiPrefillResponse> => {
    try {
        const response = await publicAxiosInstance.post<AiPrefillResponse>(
            `/sessions/share/${publicShareId}/ai-prefill`,
            {},
        );
        return response.data;
    } catch (error: any) {
        logger.error(`AI prefill failed for shareId ${publicShareId}`, error);
        throw error;
    }
};

export const applyAiPrefillForPublicShare = async (
    publicShareId: string,
    acceptedSuggestions: Array<{ field: string; suggestedValue: string }>,
): Promise<ApplyAiPrefillResponse> => {
    try {
        const response = await publicAxiosInstance.post<ApplyAiPrefillResponse>(
            `/sessions/share/${publicShareId}/ai-prefill/apply`,
            { acceptedSuggestions },
        );
        return response.data;
    } catch (error: any) {
        logger.error(`Apply AI prefill failed for shareId ${publicShareId}`, error);
        throw error;
    }
};

/**
 * Add a profession to the worker via the public-share form's "Other
 * Professions?" composite. Backend mirrors the WhatsApp bot's
 * ADD_PROFESSION_INTENT path (activate worker_profession row + seed
 * profession-specific session_questions + reset the gate). Returns the
 * refreshed { worker, questions } so the caller can `injectWrapped()` it
 * into state — the new profession's questions then appear in the form's
 * PROFESSION block above the gate.
 */
export const addProfessionForPublicShare = async (
    publicShareId: string,
    professionKey: string,
): Promise<WorkerWithSessionQuestionsResponse> => {
    try {
        const response = await publicAxiosInstance.post<WorkerWithSessionQuestionsResponse>(
            `/sessions/share/${publicShareId}/professions/add`,
            { professionKey },
        );
        return response.data;
    } catch (error: any) {
        logger.error(`Failed to add profession ${professionKey} for shareId ${publicShareId}`, error);
        throw error;
    }
};

/**
 * Trigger backend translation of the session's questions into the session's
 * current language. This is the slow path (one Gemini call, ~2-4s), so the
 * LanguagePicker shows a loading state while it's in flight. Returns the
 * wrapped { worker, questions } payload with translated strings already
 * merged in, so the caller can swap state directly without a second fetch.
 */
export const translateSessionShareQuestions = async (
    publicShareId: string,
): Promise<WorkerWithSessionQuestionsResponse> => {
    try {
        const response = await publicAxiosInstance.post<WorkerWithSessionQuestionsResponse>(
            `/sessions/share/${publicShareId}/translate-questions`,
            {},
            { timeout: 60000 },
        );
        return response.data;
    } catch (error: any) {
        logger.error(`Failed to translate session questions for shareId ${publicShareId}`, error);
        throw error;
    }
};

export const uploadSessionShareFile = async (
    publicShareId: string,
    file: File,
    fileType: PublicSessionFileType,
    options?: { onProgress?: (percent: number) => void },
): Promise<UploadSessionShareFileResponse> => {
    const form = new FormData();
    form.append('file', file);
    form.append('fileType', fileType);
    try {
        const response = await publicAxiosInstance.post<UploadSessionShareFileResponse>(
            `/sessions/share/${publicShareId}/files/upload`,
            form,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (event) => {
                    if (!options?.onProgress || !event.total) return;
                    options.onProgress(Math.round((event.loaded * 100) / event.total));
                },
            },
        );
        return response.data;
    } catch (error: any) {
        logger.error(`Failed to upload file for shareId ${publicShareId}`, error);
        throw error;
    }
};
